import { Component, OnInit, OnDestroy } from "@angular/core";
import { DatePipe } from "@angular/common";
import { HttpClient } from '@angular/common/http'
import { Observable, Subject, Subscription, concat } from "rxjs";
import { first } from 'rxjs/operators';

import * as jp from "jsonpath/jsonpath";

import { BaseControlComponent } from "../base-control/base-control.component";

import { AuthService } from "../../../services/auth.service";
import { ContactService } from "../../../admin/services/contacts.service";
import { FlowInstanceService } from "../../../flow/services/flow-instance.service";
import { LoaderService } from "../../../common/services/loader.service";
import { ReportSelectorService } from "../../services/report-selector.service";

import { Address } from "../../../common/models/address.model";
import { AgentType, Contact, SignerType } from "../../../admin/models/contact.model";
import { FlowInstance, Signature } from "../../../common/models/flow.dto";
import { SignedReportModel } from "../../../common/models/signature.model";
import { ContextInteractionService } from "../../services/context-interaction.service";


@Component({
    selector: "app-chiefs-signature",
    templateUrl: "./chiefs-signature.component.html",
    styleUrls: ["./chiefs-signature.component.scss"],
    standalone: false
})
export class ChiefsSignatureComponent extends BaseControlComponent implements OnInit, OnDestroy  {

  public static componentName = "ChiefsSignatureComponent";
  public signedRes = {};
  public recordRes = {};

  public subscription: Subscription;

  public defaultChief: Contact = <Contact>{
    internalContact: true,
    displayName: "Eric Vendel",
    firstName: "Eric",
    lastName: "Vendel",
    middleInitial: "",
    fullLegalName: "Eric Vendel",
    agentType: AgentType.None,
    emailAddress: "",
    address: {
      street: "",
      suite: "",
      poBox: "",
      city: "",
      stateCode: "",
      zipCode: ""
    } as Address,
    primaryPhone: "",
    id: "",
    organizationId: "",
    canLogIn: false,
    isUser: true,
    departmentId: ""
  };

  public chief: Contact;
  public divisionName = 'Division of Oil and Gas Resource Management';
  public profile: Contact;
  public showSignatureError: boolean = false;
  public signatureError = "An error has occurred in recording the signature.  Please try again.";

  public signer: Signature = {
    userId: "",
    legalName: "",
    signerType: SignerType.None,
    authority: "",
    flowId: ""
  };

  constructor(
    public readonly contacts: ContactService,
    public readonly instanceService: FlowInstanceService,
    public readonly loader: LoaderService,
    public reportsService: ReportSelectorService,
    public authService: AuthService,
    public readonly datePipe: DatePipe,
    public http: HttpClient,
    public readonly contextService: ContextInteractionService
  ) {
    super(contextService);
  }

  ngOnInit() {
    super.ngOnInit();

    if (!this.isEdit) {
      if (this.contextValue)
        this.signer = this.contextValue;

      this.loader.toggleLoader(true);
      this.contacts.getProfile().pipe(first()).subscribe((user) => {
        this.profile = user;
        this.loader.toggleLoader(false);
      }, (error) => {
        console.log(error);
        this.loader.toggleLoader(false);
      });
    } else {
      this.profile = this.defaultChief;
    }

    this.contacts.getInternalContactsByAuthority("Chief").pipe(first()).subscribe((users) => {
      this.chief = (users.length !== 0) ? users[0] : this.defaultChief;
    }, (error) => {
      console.log(error);
      this.chief = this.defaultChief;
    });
  }

  get chiefFullAuthorityText(): string {
    return this.isDelegateSignature ? `${this.delegateLine}${this.chief.fullLegalName}` : this.chief.fullLegalName;
  }

  get chiefLine(): string {
    return `${this.chief.fullLegalName}, Chief of the `;
  }

  get delegateLine(): string {
    let result: string = '';
    result += this.signer.authority === 'Deputy' ? 'Deputy Chief' : this.signer.authority;
    result += ` ${this.signer.legalName} for `;
    return result;
  }

  get hasAuthority(): boolean {
    return this.authService.User.authority != SignerType.None;
  }

  get isDelegateSignature(): boolean {
    return this.signer.signerType === SignerType.Deputy || this.signer.signerType === SignerType.Assistant;
  }

  get reportTemplateId(): string {
    if (this.componentContext.reporting.templateId !== null)
      return this.componentContext.reporting.templateId;
    return '';
  }

  get signature(): string {
    if (this.signer.signerType === SignerType.None) return "";
    return `/s/ ${this.signer.legalName}`;
  }

  get signatureDate(): string {
    if (this.signer.signerType === SignerType.None) return "";
    return `${this.signer.dateSigned}`;
  }

  public sign(): void {
    if (!this.profile) return;

    let signatureObj = {
      userId: this.profile.id,
      legalName: this.profile.fullLegalName,
      dateSigned: new Date(),
      flowId: this.flowComponent.workflow.id,
    } as Signature;

    let signedReport: SignedReportModel = {
      signature: this.signature,
      dateSigned: signatureObj.dateSigned,
      legalName: signatureObj.legalName,
      flowId: signatureObj.flowId,
      templateId: this.reportTemplateId == null ? '' : this.reportTemplateId,
    };

    const signedOne = this.reportsService.signReport(signedReport).pipe(first());
    const record = this.instanceService.recordChiefSignature(signatureObj);

    this.loader.toggleLoader(true);
    concat(record, signedOne).subscribe((result)=> {

    }, (error) => {
      this.showSignatureError = true;
      this.loader.toggleLoader(false);
    }, () => {
      this.signer.dateSigned = signedReport.dateSigned;
      this.signer.legalName = this.profile.fullLegalName;

      this.showSignatureError = false;
      this.loader.toggleLoader(false);
      if (this.componentContext) {
        const displayDate = this.datePipe.transform(signedReport.dateSigned);
        jp.value(this.componentContext, '$.data.chiefDateSigned', displayDate);
        jp.value(this.componentContext, '$.data.chiefLegalName', signedReport.legalName);
        jp.value(this.componentContext, '$.data.chiefSignature', signedReport.signature);
        jp.value(this.componentContext, '$.data.chiefFullAuthorityText', this.chiefFullAuthorityText);
      }
    });
  }

  ngOnDestroy() {
    if (this.subscription) { this.subscription.unsubscribe(); }
  }

}
